// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { AdvisoryAccountsDebt } from '@shared/data-access/types/brokerage-types';
import produce from 'immer';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DUAL_SERIES_AREA_CHART_COLORS, SERIES_COLORS } from '@shared/ui-kit/utils';
import { type DualSeriesChartData } from '../dual-series-area-chart/types';

const SHORT_TERM_COLOR = DUAL_SERIES_AREA_CHART_COLORS.secondary;
const LONG_TERM_COLOR = DUAL_SERIES_AREA_CHART_COLORS.primary;
const TOTAL_DEBT_COLOR = SERIES_COLORS[0]!;

export const useDebtEvolutionChart = ({
  data,
  currency,
}: {
  data?: AdvisoryAccountsDebt;
  currency: string;
}) => {
  const { t } = useTranslation();

  const aggregatedSeries = useMemo(
    () =>
      produce(
        {
          dates: [] as string[],
          shortTermValues: [] as number[],
          longTermValues: [] as number[],
        },
        draft => {
          const dates = data?.dates ?? [];
          const accounts = data?.accounts ?? [];

          draft.dates = [...dates];
          draft.shortTermValues = dates.map((_, index) =>
            accounts.reduce(
              (sum, account) =>
                sum + (account.historical_debt.short_term[index] ?? 0),
              0,
            ),
          );
          draft.longTermValues = dates.map((_, index) =>
            accounts.reduce(
              (sum, account) =>
                sum + (account.historical_debt.long_term[index] ?? 0),
              0,
            ),
          );
        },
      ),
    [data],
  );

  const chartData = useMemo(
    (): DualSeriesChartData => ({
      dates: aggregatedSeries.dates,
      series: [
        {
          id: 'short_term',
          label: t('aggregator.loans.short-term-debt', { ns: 'aggregator' }),
          values: aggregatedSeries.shortTermValues,
          color: SHORT_TERM_COLOR,
        },
        {
          id: 'long_term',
          label: t('aggregator.loans.long-term-debt', { ns: 'aggregator' }),
          values: aggregatedSeries.longTermValues,
          color: LONG_TERM_COLOR,
        },
      ],
      combinedSeries: {
        label: t('aggregator.loans.total-debt', { ns: 'aggregator' }),
        color: TOTAL_DEBT_COLOR,
      },
    }),
    [aggregatedSeries, t],
  );

  return {
    title: t('aggregator.loans.debt-evolution', { ns: 'aggregator' }),
    splitToggleLabel: t('aggregator.loans.cp-vs-lp', { ns: 'aggregator' }),
    splitToggleTooltip: t('aggregator.loans.split-view-tooltip', {
      ns: 'aggregator',
    }),
    currency,
    data: chartData,
    hasData: aggregatedSeries.dates.length > 0,
  };
};
